import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'

import { mapLanguageToComponentProps } from 'i18n'
import * as analytics from 'analytics/performance'
import config from 'config'
import VideoPlayer from 'components/video-player/video-player'
import { Subtitle } from 'components/styled/typography'
import { Button } from 'components/styled/button'
import { Flex } from 'components/styled/containers/flex'
import { Box } from 'components/styled/containers/box'
import {
  AbsoluteFillStyle,
  CenterizedContentStyle,
  FillStyle,
  RelativeContainerStyle,
} from 'theme'
import opentokVideoIsAvailable from './opentok-video-is-available'
import video from 'services/video'

const propTypes = {
  id: PropTypes.string,
  videoUrl: PropTypes.string.isRequired,
  playbackVideoUrl: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  recorder: PropTypes.string,
  playable: PropTypes.bool,
  onPlay: PropTypes.func,
  updateState: PropTypes.func,
  draftMode: PropTypes.bool,
  editMode: PropTypes.bool,
  timedInterview: PropTypes.bool,
  pauseVideo: PropTypes.bool,
  refreshPlaybackUrl: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    video: PropTypes.object,
  }).isRequired,
  locationId: PropTypes.string,
  eventId: PropTypes.string,
  videoEventProps: PropTypes.shape({}),
}

const defaultProps = {
  id: undefined,
  playable: true,
  title: '',
  recorder: '',
  onPlay: undefined,
  updateState: () => {},
  draftMode: false,
  editMode: false,
  timedInterview: false,
  pauseVideo: false,
  locationId: undefined,
  eventId: undefined,
  videoEventProps: undefined,
}

const VideoContainer = styled.div`
  ${RelativeContainerStyle};
`

export const VideoWrapper = styled.span`
  ${AbsoluteFillStyle};
  display: block;
  background-repeat: no-repeat;
  background-size: cover;

  border-radius: ${props => !props.$editMode && props.theme.space.s12};
`

const StyledVideoOverlay = styled.span`
  ${FillStyle};
  ${CenterizedContentStyle};
  border-radius: ${props => !props.$editMode && props.theme.space.s12};
  background-color: ${({ theme }) => theme.color.blackO50} !important;
`

const TitleContainer = styled(Flex)`
  width: 95%;
`

export class Video extends Component {
  constructor(props) {
    super(props)

    this.state = {
      videoPlayer: null,
      playerError: false,
      playbackVideoUrl: null,
      useOpentokPreview: false,
    }
  }

  componentDidMount() {
    const { playbackVideoUrl, recorder } = this.props
    // check playbackurl if it is not already opentok url
    if (
      playbackVideoUrl &&
      recorder &&
      recorder === 'opentok' &&
      !playbackVideoUrl.includes('archive.mp4')
    ) {
      this.getAvailableUrl()
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { videoUrl, pauseVideo } = this.props
    const { playerError, playbackVideoUrl } = this.state
    const currentVideo =
      this.state.videoPlayer !== null ? this.state.videoPlayer.video : null
    const nextVideo =
      nextState.videoPlayer !== null ? nextState.videoPlayer.video : null
    const shouldPause = !pauseVideo && nextProps.pauseVideo
    const shouldPlay = !!(
      !pauseVideo &&
      nextState.videoPlayer &&
      nextState.videoPlayer.play
    )
    return (
      videoUrl !== nextProps.videoUrl ||
      currentVideo !== nextVideo ||
      this.props.playable !== nextProps.playable ||
      playerError !== nextState.playerError ||
      shouldPause ||
      playbackVideoUrl !== nextState.playbackVideoUrl ||
      shouldPlay
    )
  }

  componentDidUpdate(prevProps) {
    const { recorder, playbackVideoUrl } = this.props
    const { videoPlayer } = this.state

    if (prevProps.playbackVideoUrl !== playbackVideoUrl) {
      // if playbackUrl changes and player is playing we pause it and set videoplayer null
      // so won't play with previous playbackurl
      if (videoPlayer) {
        this.onFinish()
      }

      // check playbackurl if it is not already opentok url
      if (
        playbackVideoUrl &&
        recorder &&
        recorder === 'opentok' &&
        !playbackVideoUrl.includes('archive.mp4')
      ) {
        this.getAvailableUrl()
      } else {
        this.reload()
      }
    }
  }

  onPlay(play, time) {
    const {
      playable,
      onPlay,
      id,
      locationId,
      videoEventProps,
      recorder,
      eventId,
    } = this.props
    const video = this.getVideo()
    const videoPlayer = { play, video }

    if (!playable) {
      return
    }
    if (time) {
      videoPlayer.time = time
    }
    this.setState({ videoPlayer })

    if (play && onPlay) {
      onPlay({ id })
    }

    analytics.onClickEvent({
      locationId,
      id: eventId || 'questionPlayButton',
      properties: {
        ...videoEventProps,
        recorder,
        hasBackground:
          recorder === 'mediarecorder' || recorder === 'mediarecorder-bg',
      },
    })
  }

  onProgress(time) {
    const { videoPlayer } = this.state
    this.setState({ videoPlayer: { ...videoPlayer, time } })
  }

  onFinish() {
    const { updateState } = this.props
    this.setState({ videoPlayer: null })
    updateState('pause')
  }

  onError() {
    const { updateState } = this.props
    this.setState({ playerError: true })
    updateState('error')
  }

  getAvailableUrl() {
    const { playbackVideoUrl } = this.props
    opentokVideoIsAvailable(playbackVideoUrl).then(available => {
      const useOpentokPreview = !available
      this.setState({ useOpentokPreview })
      this.reload(useOpentokPreview)
    })
  }

  getVideo() {
    if (typeof this.props === 'undefined') {
      return null
    }

    const { id, videoUrl } = this.props
    return id || videoUrl
  }

  reload(useOpentokPreview) {
    const { refreshPlaybackUrl } = this.props
    return refreshPlaybackUrl(useOpentokPreview).then(playbackVideoUrl =>
      this.setState({ playerError: false, playbackVideoUrl })
    )
  }

  isPlayerEnabled() {
    const { playable } = this.props
    const { videoPlayer } = this.state
    return playable && videoPlayer && videoPlayer.video === this.getVideo()
  }

  renderVideo() {
    const {
      id,
      i18n,
      videoUrl,
      playbackVideoUrl,
      title,
      playable,
      updateState,
      draftMode,
      editMode,
      pauseVideo,
      timedInterview,
    } = this.props
    const { useOpentokPreview, videoPlayer } = this.state

    const { playerError, playbackVideoUrl: refreshedUrl } = this.state
    const imageName = videoUrl.replace(/\?.*$/, '')
    const backgroundImage = `url('${config.USER_UPLOADS_BASE_URL}/b2c-screenshots/${imageName}.jpg')`

    if (playerError) {
      return (
        <VideoWrapper className="video-content">
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
          >
            <Box my="s20">
              <Subtitle>{i18n.video.videoNotFound}</Subtitle>
            </Box>
            <Button
              variant="secondary"
              icon="refresh"
              text={i18n.video.retry}
              onClick={() => {
                this.reload(useOpentokPreview)
              }}
            />
          </Flex>
        </VideoWrapper>
      )
    }

    if (this.isPlayerEnabled()) {
      return (
        <VideoPlayer
          videoUrl={videoUrl}
          playbackVideoUrl={`${refreshedUrl || playbackVideoUrl}`}
          onFinish={() => {
            this.onFinish()
          }}
          onError={() => {
            this.onError()
          }}
          updateState={updateState}
          draftMode={draftMode}
          timedInterview={timedInterview}
          pauseVideo={pauseVideo}
          videoPlayer={videoPlayer}
        />
      )
    }
    // Don't know why we use spans here instead of divs but
    // don't ever try to change these to divs or something evil will happen!
    // Flowplayer won't ever disappear from screen if these are not spans and
    // your whole day is ruined
    return (
      <VideoWrapper
        id={id}
        className="video-content"
        style={{ backgroundImage }}
        $editMode={editMode}
      >
        <StyledVideoOverlay $editMode={editMode} className="video-overlay">
          <TitleContainer
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            className="title-container"
          >
            {typeof title === 'string' ? (
              <Box mb="s20">
                <Subtitle color="white">{title}</Subtitle>
              </Box>
            ) : (
              title
            )}
            {playable && (
              <Button
                variant="tertiaryGhost"
                icon="play_arrow"
                onClick={() => this.onPlay(true, 0)}
              />
            )}
          </TitleContainer>
        </StyledVideoOverlay>
      </VideoWrapper>
    )
  }

  render() {
    const { playable } = this.props
    return (
      <VideoContainer className={`video${playable ? ' playable' : ''}`}>
        {this.renderVideo()}
      </VideoContainer>
    )
  }
}

Video.propTypes = propTypes
Video.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps())(Video)
